// v0.30 — "Put your money to work": the Cash ⇄ Earning allocation surface.
//
// Earning is held as **M-MMF** (Mtoken), a permissioned ERC-20 on Arc whose
// NAV per unit accrues. Cash is USDC. The user sees one USD number on the home
// pill; this sheet is where the split is chosen.
//
// The chain side does not exist yet (MToken / MTeller / MEligibility ship with
// the contracts). Everything here talks to an `EarnSource`, so wiring the real
// Teller later is a one-file change and the UI does not move.

/** A wallet's M-MMF position. `usd` = units × navPerUnit. */
export interface EarnPosition {
  units: number;
  navPerUnit: number;
  usd: number;
}

/**
 * The seam. `PlaceholderEarnSource` reports a zero position and refuses moves;
 * `TellerEarnSource` (v0.30.2) will read `balanceOf`/`pricePerShare` from Arc
 * and submit a userOp to MTeller.
 */
export interface EarnSource {
  getPosition(address: string): Promise<EarnPosition>;
  /** Annualised rate as a percentage (4.21 = 4.21%), or null if unknown. */
  getApy(): Promise<number | null>;
  /** Cash → Earning. Subscribe: pull USDC, mint M-MMF at NAV.
   *  v0.90.26 — resolves at ACCEPTANCE; `settled` follows the receipt. */
  moveToEarning(address: string, usd: number): Promise<EarnMove | void>;
  /** Earning → Cash. Redeem: burn M-MMF at NAV, pay USDC from the buffer. */
  moveToCash(address: string, usd: number): Promise<EarnMove | void>;
}

/** v0.90.26 — a move the bundler has accepted; `settled` is true once the receipt says success. */
export interface EarnMove {
  settled: Promise<boolean>;
}

/** Thrown by the placeholder so the UI can say something honest. */
export class EarnUnavailableError extends Error {
  constructor() {
    super("Earning is not live on this build yet.");
    this.name = "EarnUnavailableError";
  }
}

export const placeholderEarnSource: EarnSource = {
  async getPosition() {
    return { units: 0, navPerUnit: 1, usd: 0 };
  },
  async getApy() {
    return null;
  },
  async moveToEarning() {
    throw new EarnUnavailableError();
  },
  async moveToCash() {
    throw new EarnUnavailableError();
  },
};

// ---- allocation rules ----------------------------------------------------

/**
 * No cash floor. Gas on Arc is USDC and sponsored by Circle's Gas Station, so
 * a $0 cash balance is not a dead end — the customer may put the whole balance
 * to work. "Max" therefore means the entire balance.
 */
export function maxEarning(totalUsd: number): number {
  return Math.max(0, Math.floor(totalUsd));
}

/**
 * A percentage chip resolves against the **total balance** (the number on the
 * pill), floored to a whole dollar and clamped to the ceiling above.
 */
export function chipAmount(totalUsd: number, pct: number): number {
  return Math.min(maxEarning(totalUsd), Math.floor((totalUsd * pct) / 100));
}

/**
 * Whole-dollar money display for figures that can't carry a remainder — the
 * Earning pocket (integer slider) and the combined total. Whole dollars, with
 * cents shown only under $10 where they dominate the number.
 */
export function formatMoney(n: number): string {
  const v = Number.isFinite(n) ? Math.max(0, n) : 0;
  if (v < 10) return `$${v.toFixed(2)}`;
  return `$${Math.floor(v).toLocaleString("en-US")}`;
}

/**
 * Cash display: always to the cent. Cash is the pocket that carries the
 * sub-dollar remainder (the slider moves whole dollars into Earning), so it
 * must show cents at every size — otherwise a $60.32 balance reads "$60" in
 * the sheet while the combined total reads "$60.32", and the two disagree.
 */
export function formatCash(n: number): string {
  return `$${formatCents(n)}`;
}

/**
 * Bare 2dp number (no "$") — for the home pill, whose markup renders the "$"
 * separately. Always shows cents.
 */
export function formatCents(n: number): string {
  const v = Number.isFinite(n) ? Math.max(0, n) : 0;
  return v.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** @deprecated use formatMoney. */
export const formatEarning = formatMoney;

// ---- the sheet -----------------------------------------------------------

export interface EarnSheetDeps {
  address: string;
  /** Spendable USDC, in dollars. */
  cashUsd: number;
  source: EarnSource;
  toast: (msg: string) => void;
  /** Called after a successful move so the host can refresh balances. */
  onMoved?: () => void;
  /** Called to dismiss the sheet after a successful move. */
  onDone?: () => void;
}

const PCTS = [25, 50, 75] as const;

export function renderEarnSheet(body: HTMLElement, deps: EarnSheetDeps): void {
  const { address, cashUsd, source } = deps;

  body.innerHTML = `
    <div class="earn-head">
      <h3>Put your money to work</h3>
      <p>Earn every day. Spend any time.</p>
    </div>
    <div id="earnBody">
      <div class="add-progress"><span class="spinner"></span>Loading…</div>
    </div>`;

  const mount = body.querySelector<HTMLElement>("#earnBody")!;

  void (async () => {
    let position: EarnPosition;
    let apy: number | null;
    try {
      [position, apy] = await Promise.all([
        source.getPosition(address),
        source.getApy(),
      ]);
    } catch {
      mount.innerHTML = `<p class="sheet-note">Couldn't load your position. Pull down and try again.</p>`;
      return;
    }

    const total = cashUsd + position.usd;
    const ceiling = maxEarning(total);

    // Slider units are whole dollars; movement is continuous across the range
    // (no detents), so the step is $1 and the range scales with the balance.
    let value = Math.min(ceiling, Math.round(position.usd));

    mount.innerHTML = `
      <div class="earn-split">
        <div class="earn-pocket cash">
          <div class="plab">Cash</div>
          <div class="pval" id="earnCash"></div>
        </div>
        <div class="earn-pocket earn">
          <div class="plab">Earning</div>
          <div class="pval" id="earnMmf"></div>
        </div>
      </div>

      <div class="earn-trk">
        <input id="earnRange" type="range" min="0" max="${ceiling}" step="1"
               value="${value}" aria-label="Amount to put to work" />
      </div>

      <div class="earn-chips">
        ${PCTS.map(
          (p) => `<button class="earn-chip" type="button" data-pct="${p}">${p}%</button>`,
        ).join("")}
        <button class="earn-chip" type="button" data-pct="max">Max</button>
      </div>

      <div class="earn-rate" id="earnRate"></div>
      <button class="earn-cta" id="earnCta" type="button"></button>
      <p class="earn-foot">Held as M-MMF. See our <a href="/terms" target="_blank" rel="noopener">Terms &amp; Conditions</a>.</p>`;

    const range = mount.querySelector<HTMLInputElement>("#earnRange")!;
    const elCash = mount.querySelector<HTMLElement>("#earnCash")!;
    const elMmf = mount.querySelector<HTMLElement>("#earnMmf")!;
    const elRate = mount.querySelector<HTMLElement>("#earnRate")!;
    const cta = mount.querySelector<HTMLButtonElement>("#earnCta")!;
    const chips = Array.from(
      mount.querySelectorAll<HTMLButtonElement>(".earn-chip"),
    );

    elRate.innerHTML =
      apy == null
        ? `<span class="muted">Rate shown once Earning is live</span>`
        : `Currently earning <b>${apy.toFixed(2)}%</b> a year`;

    if (ceiling <= 0) {
      range.disabled = true;
      chips.forEach((c) => (c.disabled = true));
    }

    // When the slider is at its ceiling, "Earning" means the whole balance —
    // cents included — so cash reads a true $0. Below the ceiling the slider
    // produces integer moves and cash carries the remainder.
    // v0.90.22 — until the slider is touched, the sheet shows the position as
    // it is: the true Earning (cents included) and the true Cash. Before, the
    // slider's whole-dollar value stood in for Earning at rest, so Cash was
    // wrong by the position's cents (Safari showed $277.64 / $128 for a true
    // $277.48 / $128.16). The whole-dollar preview starts on the first move.
    let touched = false;
    function earningUsd(): number {
      if (!touched) return position.usd;
      return value >= ceiling ? total : value;
    }

    function paint() {
      const atMax = touched && value >= ceiling;
      const earning = earningUsd();
      const cash = touched ? Math.max(0, total - earning) : cashUsd;

      // Cash carries the sub-dollar remainder below Max, so it always shows
      // cents. Earning is whole-dollar EXCEPT at Max, where all cash (cents
      // included) has moved across — then Earning holds the remainder and shows
      // 2dp, and cash reads a true $0.00.
      elCash.textContent = formatCash(cash);
      elMmf.textContent = atMax || !touched ? formatCash(earning) : formatMoney(earning);
      elMmf.classList.toggle("idle", earning <= 0);

      // Track stays neutral; the knob carries the colour — cyan when every
      // dollar is still cash, green the moment any of it is at work.
      range.classList.toggle("at-zero", earning <= 0);

      // A chip lights green only when it matches the current value exactly.
      chips.forEach((c) => {
        const raw = c.dataset.pct!;
        const target =
          raw === "max" ? ceiling : chipAmount(total, Number(raw));
        c.classList.toggle("on", touched && ceiling > 0 && target === value);
      });

      const delta = earning - position.usd;
      const moving = Math.abs(delta) >= 0.005;
      cta.classList.toggle("cy", delta < 0);
      cta.disabled = !moving;
      cta.textContent =
        delta < 0
          ? "Move to Cash"
          : position.usd > 0
            ? "Move to Earning"
            : "Start earning";
    }

    range.addEventListener("input", () => {
      touched = true;
      value = Number(range.value) || 0;
      paint();
    });

    chips.forEach((c) => {
      c.addEventListener("click", () => {
        touched = true;
        const raw = c.dataset.pct!;
        value = raw === "max" ? ceiling : chipAmount(total, Number(raw));
        range.value = String(value);
        paint();
      });
    });

    cta.addEventListener("click", () => {
      const delta = earningUsd() - position.usd;
      if (Math.abs(delta) < 0.005) return;
      const movingToEarning = delta > 0;
      const moved = Math.abs(delta);
      // Moving to earning: state the TOTAL now earning (the new MMF position),
      // since that's the balance the user cares about. Moving to cash: state
      // the amount moved back (the delta), since that's what just became
      // spendable.
      const totalEarning = earningUsd();
      cta.disabled = true;

      // Replace the sheet body with a progress state while the userOp is signed
      // and submitted — then a success card the user dismisses themselves. This
      // avoids the old behaviour of closing the instant the tx was submitted
      // (no confirmation, and a balance refresh that raced the close).
      void (async () => {
        try {
          // v0.90.26 — the card shows at acceptance; the position re-reads
          // now and again once the receipt lands (deps.onMoved twice).
          const mv = movingToEarning
            ? await source.moveToEarning(address, delta)
            : await source.moveToCash(address, -delta);
          if (mv && mv.settled) void mv.settled.then(() => deps.onMoved?.()).catch(() => {});

          // Success — show a confirmation the user closes when ready. No
          // header here: the sheet's outer earn-head is still present above
          // #earnBody, so rendering another would duplicate it.
          mount.innerHTML = `
            <div class="earn-done">
              <div class="tickbig">\u2713</div>
              <p class="earn-done-msg">${
                movingToEarning
                  ? `${formatMoney(totalEarning)} now earning`
                  : `${formatMoney(moved)} moved to cash`
              }</p>
              <button class="earn-cta" id="earnDoneBtn" type="button">Done</button>
            </div>`;
          // Refresh balances in the background — the sheet stays put until the
          // user taps Done, so a slow chain read can't race the close.
          deps.onMoved?.();
          mount
            .querySelector<HTMLButtonElement>("#earnDoneBtn")!
            .addEventListener("click", () => deps.onDone?.());
        } catch (err) {
          cta.disabled = false;
          console.error("earn move failed:", err);
          showError(
            err instanceof EarnUnavailableError
              ? "Earning goes live with the M-MMF contract."
              : "Couldn't move that right now — try again.",
          );
        }
      })();
    });

    // Error shows as cyan text in place of the yield line above the CTA — no
    // panel, no pill (both read as tappable). Restored on the next interaction.
    const rateHtml = elRate.innerHTML;
    function showError(msg: string) {
      elRate.innerHTML = `<span class="earn-err">${msg}</span>`;
    }
    function clearError() {
      if (elRate.querySelector(".earn-err")) elRate.innerHTML = rateHtml;
    }
    range.addEventListener("input", clearError);
    chips.forEach((c) => c.addEventListener("click", clearError));

    paint();
  })();
}
